import type {
  CategoryId,
  ProcurementType,
  Supplier,
  SupplierId,
} from './types';

/**
 * All suppliers except Gently AI are fictional, with one exception: MRO Supply
 * Co., Border States, Brazos Valley Logistics and Sentinel Freight are the
 * Pillar-cast vendors from the AP and Replenishment demos, so the whole demo
 * branch buys from one supplier universe. Spend, OTIF, PPM, risk and contract
 * ids are the canonical numbers from the demo README.
 */
export const SUPPLIERS: Record<SupplierId, Supplier> = {
  'sup-corvane': {
    id: 'sup-corvane',
    name: 'Corvane Wire & Cable, Inc.',
    shortName: 'Corvane',
    hq: { city: 'Carrollton, GA', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-wire',
    segment: 'Strategic',
    spendTtm: 48_200_000,
    otif: 91.0,
    ppm: 820,
    riskScore: 64,
    riskLevel: 'high',
    riskNote:
      'Credit score downgraded from 72 to 58 and 4 quality issues in the last 12 months. On the credit watch list ahead of the December renewal.',
    contractIds: ['SA-CORV-2024-017'],
    sites: ['DAL', 'PHX'],
    materialIds: ['MAT-THHN'],
    supplierSince: 2014,
    description:
      'Georgia wire mill producing copper building wire and feeder cable. Primary source of 1/0 gauge copper building wire for the Dallas and Phoenix DCs and the cut-to-length wire service.',
    primaryContact: { name: 'Jonas Weber', title: 'Key Account Director' },
    paymentTerms: 'Net 45',
    certifications: [
      'UL listed (building wire)',
      'Canadian safety certified',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 58,
    creditScorePrior: 72,
    duns: '314829176',
    bank: {
      bankName: 'Chattahoochee Commerce Bank',
      accountMasked: '•••• 4821',
      verified: true,
    },
    ecoVadis: 71,
  },
  'sup-hollis': {
    id: 'sup-hollis',
    name: 'Hollis Electric Products, Inc.',
    shortName: 'Hollis',
    hq: { city: 'Jackson, TN', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-wire',
    segment: 'Strategic',
    spendTtm: 31_600_000,
    otif: 98.1,
    ppm: 140,
    riskScore: 18,
    riskLevel: 'low',
    riskNote:
      'Reliable delivery and quality with no open findings. Capacity headroom confirmed for an increased share.',
    contractIds: ['SA-HOLL-2024-021'],
    sites: ['DAL', 'PHX', 'PNL'],
    materialIds: ['MAT-THHN', 'MAT-MCCB'],
    supplierSince: 2011,
    description:
      'Tennessee manufacturer of building wire, cable and circuit breakers. Second source for copper building wire and the approved backup for 800 A breakers.',
    primaryContact: {
      name: 'Brian Caldwell',
      title: 'VP Commercial, Wire and Circuit Protection',
    },
    paymentTerms: 'Net 45',
    certifications: [
      'UL listed (building wire)',
      'UL listed (circuit breakers)',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 86,
    duns: '078265413',
    bank: {
      bankName: 'Madison County Trust Bank',
      accountMasked: '•••• 7390',
      verified: true,
    },
    ecoVadis: 78,
  },
  'sup-kaisei': {
    id: 'sup-kaisei',
    name: 'Kaisei Electric Wire Co., Ltd.',
    shortName: 'Kaisei',
    hq: { city: 'Osaka', country: 'Japan' },
    type: 'direct',
    categoryId: 'cat-wire',
    segment: 'Preferred',
    spendTtm: 12_400_000,
    otif: 94.0,
    ppm: 210,
    riskScore: 41,
    riskLevel: 'medium',
    riskNote:
      'Single manufacturing plant in an earthquake zone. 12-week lead time and yen-denominated pricing add supply and currency exposure.',
    contractIds: ['SA-KAIS-2025-003'],
    sites: ['DAL', 'PHX'],
    materialIds: ['MAT-THHN'],
    supplierSince: 2019,
    description:
      'Japanese wire maker supplying high-conductivity copper building wire from a single plant near Osaka.',
    primaryContact: {
      name: 'Haruto Matsuda',
      title: 'General Manager, Overseas Sales',
    },
    paymentTerms: 'Net 60',
    certifications: ['UL listed (building wire)', 'ISO 9001:2015', 'ISO 14001'],
    creditScore: 79,
    duns: '690417258',
    bank: {
      bankName: 'Naniwa Mercantile Bank',
      accountMasked: '•••• 1156',
      verified: true,
    },
    ecoVadis: 64,
  },
  'sup-vitrex': {
    id: 'sup-vitrex',
    name: 'Vitrex Electric AG',
    shortName: 'Vitrex',
    hq: { city: 'Basel', country: 'Switzerland' },
    type: 'direct',
    categoryId: 'cat-circuit',
    segment: 'Strategic',
    spendTtm: 11_200_000,
    otif: 96.2,
    ppm: 1450,
    riskScore: 82,
    riskLevel: 'critical',
    riskNote:
      'Open corrective action request (SCAR-0192) on lot VX-24117. Breakers shipped from a factory that UL has not certified, which violates the quality agreement (§7.2).',
    contractIds: ['SA-VITX-2023-004', 'QA-VITX-2023-004'],
    sites: ['PNL', 'DAL'],
    materialIds: ['MAT-MCCB'],
    supplierSince: 2018,
    description:
      'Swiss maker of circuit breakers, supplying 800 A frame breakers to the Dallas panel shop and DC under a supply agreement and a quality agreement.',
    primaryContact: {
      name: 'Dr. Lukas Brenner',
      title: 'Head of North America Accounts',
    },
    paymentTerms: 'Net 60',
    certifications: [
      'UL listed (circuit breakers)',
      'EU safety certified',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 71,
    duns: '482903765',
    bank: {
      bankName: 'Rhenus Privatbank AG',
      accountMasked: '•••• 6048',
      verified: true,
    },
    ecoVadis: 69,
  },
  'sup-aurelia': {
    id: 'sup-aurelia',
    name: 'Aurelia Lighting S.A. de C.V.',
    shortName: 'Aurelia',
    hq: { city: 'Monterrey', country: 'Mexico' },
    type: 'direct',
    categoryId: 'cat-lighting',
    segment: 'Strategic',
    spendTtm: 22_900_000,
    otif: 97.0,
    ppm: 95,
    riskScore: 15,
    riskLevel: 'low',
    riskNote:
      'No open findings. Strong delivery and quality record, and a recyclable packaging roadmap in the contract.',
    contractIds: ['SA-AURE-2024-009'],
    sites: ['DAL', 'PHX', 'HAY'],
    materialIds: ['MAT-LED'],
    supplierSince: 2015,
    description:
      'Monterrey LED light fixture manufacturer building the Pillar Pro high-bay line to Pillar specifications. Incumbent in the Pillar Pro LED high-bay bids.',
    primaryContact: {
      name: 'Mariana Treviño',
      title: 'Commercial Director, Private Label Programs',
    },
    paymentTerms: 'Net 45, 2% off in 10 days',
    certifications: [
      'UL listed (light fixtures)',
      'DLC Premium (energy listed)',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 84,
    duns: '541086392',
    bank: {
      bankName: 'Banco Sierra Madre',
      accountMasked: '•••• 2275',
      verified: true,
    },
    ecoVadis: 82,
  },
  'sup-northgate': {
    id: 'sup-northgate',
    name: 'Northgate Lighting LLC',
    shortName: 'Northgate',
    hq: { city: 'Akron, OH', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-lighting',
    segment: 'Approved',
    spendTtm: 9_800_000,
    otif: 93.0,
    ppm: 160,
    riskScore: 22,
    riskLevel: 'low',
    riskNote:
      'Stable domestic supplier. Limited capacity above roughly 40% share of high-bay volume.',
    contractIds: ['SA-NGTE-2024-012'],
    sites: ['DAL', 'PHX', 'HAY'],
    materialIds: ['MAT-LED'],
    supplierSince: 2018,
    description:
      'Ohio light fixture assembler supplying LED high-bay fixtures to Pillar Pro specifications, with short domestic lead times.',
    primaryContact: { name: 'Dale Pruitt', title: 'Regional Sales Manager' },
    paymentTerms: 'Net 30',
    certifications: [
      'UL listed (light fixtures)',
      'DLC Premium (energy listed)',
      'ISO 9001:2015',
    ],
    creditScore: 77,
    duns: '205918374',
    bank: {
      bankName: 'Summit County Trust',
      accountMasked: '•••• 9034',
      verified: true,
    },
    ecoVadis: 61,
  },
  'sup-brightpak': {
    id: 'sup-brightpak',
    name: 'Brightpak Lighting Technology (Shenzhen) Co.',
    shortName: 'Brightpak',
    hq: { city: 'Shenzhen', country: 'China' },
    type: 'direct',
    categoryId: 'cat-lighting',
    segment: 'Approved',
    spendTtm: 6_100_000,
    otif: 88.5,
    ppm: 410,
    riskScore: 71,
    riskLevel: 'high',
    riskNote:
      '30% tariff exposure and a forced-labor import risk on the aluminium used in LED housings, found deeper in its supply chain. No agreement in place; bought one purchase order at a time.',
    contractIds: [],
    sites: ['PHX'],
    materialIds: [],
    supplierSince: 2023,
    description:
      'Shenzhen LED light fixture manufacturer offering low-cost high-bay and area lighting, bought one purchase order at a time.',
    primaryContact: { name: 'Lin Wei', title: 'Export Sales Manager' },
    paymentTerms: 'Net 30',
    certifications: ['UL listed (select models)', 'ISO 9001:2015'],
    creditScore: 62,
    duns: '529740816',
    bank: {
      bankName: 'Pearl River Commercial Bank',
      accountMasked: '•••• 3317',
      verified: true,
    },
    ecoVadis: 44,
  },
  'sup-meridian': {
    id: 'sup-meridian',
    name: 'Meridian Plastics & Conduit LP',
    shortName: 'Meridian',
    hq: { city: 'Houston, TX', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-conduit',
    segment: 'Preferred',
    spendTtm: 14_300_000,
    otif: 95.5,
    ppm: 120,
    riskScore: 20,
    riskLevel: 'low',
    riskNote:
      'Stable. Pricing follows the PVC resin market price, so cost moves with the commodity cycle.',
    contractIds: ['SA-MERI-2025-001'],
    sites: ['DAL'],
    materialIds: ['MAT-PVC'],
    supplierSince: 2017,
    description:
      'Texas maker of PVC conduit in standard and heavy wall, plus fittings and duct. Sole source of standard-wall PVC conduit for the Dallas DC.',
    primaryContact: {
      name: 'Carla Ortega',
      title: 'Account Manager, Conduit and Fittings',
    },
    paymentTerms: 'Net 45',
    certifications: [
      'UL listed (conduit)',
      'Meets industry conduit standard',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 88,
    duns: '163052847',
    bank: {
      bankName: 'Gulf Coast Industrial Bank',
      accountMasked: '•••• 5582',
      verified: true,
    },
    ecoVadis: 66,
  },
  'sup-lumen': {
    id: 'sup-lumen',
    name: 'Lumen Drive Technology Co., Ltd.',
    shortName: 'Lumen Drive',
    hq: { city: 'Ningbo', country: 'China' },
    type: 'direct',
    categoryId: 'cat-drives',
    segment: 'Strategic',
    spendTtm: 18_700_000,
    otif: 92.0,
    ppm: 180,
    riskScore: 74,
    riskLevel: 'high',
    riskNote:
      'Sole source for 50 HP motor drives, with China-origin tariff exposure. A second source is in onboarding.',
    contractIds: ['SA-LUMN-2025-002'],
    sites: ['HAY', 'PNL'],
    materialIds: ['MAT-VFD'],
    supplierSince: 2021,
    description:
      'Chinese maker of low-voltage motor drives and motor starters, and the sole source for the 50 HP 480 V motor drives Pillar resells and builds into retrofit kits.',
    primaryContact: {
      name: 'Zhang Yilin',
      title: 'Director of International Sales',
    },
    paymentTerms: 'Net 60',
    certifications: [
      'UL listed (motor drives)',
      'EU safety certified',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 66,
    duns: '427158906',
    bank: {
      bankName: 'Yongjiang Commercial Bank',
      accountMasked: '•••• 8803',
      verified: true,
    },
    ecoVadis: 52,
  },
  'sup-kestrel': {
    id: 'sup-kestrel',
    name: 'Kestrel Drives A/S',
    shortName: 'Kestrel',
    hq: { city: 'Nordborg', country: 'Denmark' },
    type: 'direct',
    categoryId: 'cat-drives',
    segment: 'Onboarding',
    spendTtm: 0,
    otif: null,
    ppm: null,
    riskScore: 24,
    riskLevel: 'low',
    riskNote:
      'Preliminary score from onboarding screening. No adverse findings; bank account ownership verified.',
    contractIds: ['NDA-KEST-2026-001'],
    sites: ['HAY', 'PNL'],
    materialIds: ['MAT-VFD'],
    supplierSince: null,
    description:
      'Danish drives manufacturer being approved as the second source for 50 HP 480 V motor drives.',
    primaryContact: {
      name: 'Mikkel Andersen',
      title: 'Head of Business Development',
    },
    paymentTerms: 'Net 60 (proposed)',
    certifications: [
      'UL listed (motor drives)',
      'EU safety certified',
      'ISO 9001:2015',
    ],
    creditScore: 81,
    duns: '305671289',
    bank: {
      bankName: 'Danske Bank',
      accountMasked: '•••• 4471',
      verified: true,
    },
    ecoVadis: 72,
  },
  'sup-polaris': {
    id: 'sup-polaris',
    name: 'Polaris Enclosures, Inc.',
    shortName: 'Polaris',
    hq: { city: 'Spartanburg, SC', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-enclosures',
    segment: 'Strategic',
    spendTtm: 16_500_000,
    otif: 96.0,
    ppm: 130,
    riskScore: 46,
    riskLevel: 'medium',
    riskNote:
      'Single source for watertight enclosures. No approved alternate for the 24 x 20 in watertight enclosure used in motor drive retrofit kits.',
    contractIds: ['SA-POLR-2022-008'],
    sites: ['PNL', 'DAL'],
    materialIds: [],
    supplierSince: 2012,
    description:
      'South Carolina fabricator of watertight and dust-tight enclosures, sub-panels and wireways for industrial control panels.',
    primaryContact: { name: 'Randall Boyd', title: 'VP Sales' },
    paymentTerms: 'Net 45',
    certifications: [
      'UL listed (enclosures)',
      'Canadian safety certified',
      'ISO 9001:2015',
    ],
    creditScore: 81,
    duns: '712384095',
    bank: {
      bankName: 'Piedmont Trust & Savings',
      accountMasked: '•••• 2960',
      verified: true,
    },
    ecoVadis: 63,
  },
  'sup-clearwell': {
    id: 'sup-clearwell',
    name: 'Clearwell Wiring Devices',
    shortName: 'Clearwell',
    hq: { city: 'Edison, NJ', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-devices',
    segment: 'Preferred',
    spendTtm: 5_200_000,
    otif: 97.5,
    ppm: 60,
    riskScore: 12,
    riskLevel: 'low',
    riskNote: 'No material risk signals.',
    contractIds: ['SA-CLRW-2024-006'],
    sites: ['DAL', 'PHX', 'HAY'],
    materialIds: [],
    supplierSince: 2016,
    description:
      'New Jersey maker of commercial and industrial receptacles, switches, dimmers and wall plates.',
    primaryContact: { name: 'Nina Shah', title: 'Senior Account Manager' },
    paymentTerms: 'Net 45',
    certifications: [
      'UL listed (wiring devices)',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 85,
    duns: '846209137',
    bank: {
      bankName: 'Raritan Valley Bank',
      accountMasked: '•••• 7714',
      verified: true,
    },
    ecoVadis: 70,
  },
  'sup-stellar': {
    id: 'sup-stellar',
    name: 'Stellar Transformer Co.',
    shortName: 'Stellar',
    hq: { city: 'Mentor, OH', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-power',
    segment: 'Strategic',
    spendTtm: 7_400_000,
    otif: 95.0,
    ppm: 40,
    riskScore: 58,
    riskLevel: 'medium',
    riskNote:
      'A new federal energy-efficiency rule and a shortage of special transformer steel put capacity at risk. Lead times are over 50 weeks.',
    contractIds: ['SA-STLR-2024-005'],
    sites: ['DAL', 'PNL'],
    materialIds: [],
    supplierSince: 2013,
    description:
      'Ohio manufacturer of dry-type distribution and control power transformers, supplying the Dallas DC and the panel shop under a supply agreement.',
    primaryContact: {
      name: 'Tony Ferraro',
      title: 'Director, Customer Programs',
    },
    paymentTerms: 'Net 45',
    certifications: [
      'UL listed (transformers)',
      'Meets US energy-efficiency rules',
      'ISO 9001:2015',
    ],
    creditScore: 78,
    duns: '593821064',
    bank: {
      bankName: 'Lakeshore Commerce Bank',
      accountMasked: '•••• 3481',
      verified: true,
    },
    ecoVadis: 58,
  },
  'sup-gently': {
    id: 'sup-gently',
    name: 'Gently AI, Inc.',
    shortName: 'Gently AI',
    hq: { city: 'New York, NY', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-it-software',
    segment: 'Preferred',
    spendTtm: 480_000,
    otif: null,
    ppm: null,
    riskScore: 9,
    riskLevel: 'low',
    riskNote: 'SOC 2 Type II report is current. No open findings.',
    contractIds: ['MSA-GEN-2025-001'],
    sites: ['HQ'],
    materialIds: [],
    supplierSince: 2025,
    description:
      'AI-native ERP and agentic AP / procurement automation platform.',
    primaryContact: { name: 'Gently account team', title: 'Customer success' },
    paymentTerms: 'Net 45',
    certifications: ['SOC 2 Type II', 'ISO 27001:2022'],
    creditScore: 74,
    duns: '968140352',
    bank: {
      bankName: 'Hudson Square Bank',
      accountMasked: '•••• 0629',
      verified: true,
    },
    ecoVadis: null,
  },
  'sup-stratus': {
    id: 'sup-stratus',
    name: 'Stratus Cloud Services',
    shortName: 'Stratus',
    hq: { city: 'Seattle, WA', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-it-software',
    segment: 'Strategic',
    spendTtm: 12_200_000,
    otif: null,
    ppm: null,
    riskScore: 52,
    riskLevel: 'medium',
    riskNote:
      'Security incident reported in the last 30 days. Customer data exposure is being assessed against the enterprise agreement and its data protection terms.',
    contractIds: ['EA-STRT-2024-001'],
    sites: ['DAL', 'PHX', 'HAY', 'PNL', 'HQ'],
    materialIds: [],
    supplierSince: 2019,
    description:
      'Enterprise cloud infrastructure and platform services provider hosting the analytics, integration and ERP workloads of Pillar Industrial Supply.',
    primaryContact: {
      name: 'Megan Ostrowski',
      title: 'Enterprise Account Executive',
    },
    paymentTerms: 'Net 45',
    certifications: [
      'SOC 2 Type II',
      'ISO 27001:2022',
      'ISO 27017:2015',
      'ISO 27018:2019',
    ],
    creditScore: 90,
    duns: '183947260',
    bank: {
      bankName: 'Cascade Pacific Bank',
      accountMasked: '•••• 8120',
      verified: true,
    },
    ecoVadis: 80,
  },
  'sup-northstar': {
    id: 'sup-northstar',
    name: 'Northstar Technology Solutions',
    shortName: 'Northstar',
    hq: { city: 'Dallas, TX', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-it-hardware',
    segment: 'Preferred',
    spendTtm: 4_100_000,
    otif: null,
    ppm: null,
    riskScore: 14,
    riskLevel: 'low',
    riskNote: 'No material risk signals.',
    contractIds: ['CPA-NSTR-2025-002'],
    sites: ['HQ', 'DAL', 'HAY'],
    materialIds: [],
    supplierSince: 2020,
    description:
      'IT hardware reseller supplying laptops, peripherals and networking gear under a catalog pricing agreement.',
    primaryContact: { name: 'Kyle Brandt', title: 'Strategic Account Manager' },
    paymentTerms: 'Net 30',
    certifications: ['ISO 9001:2015', 'ISO 27001:2022'],
    creditScore: 80,
    duns: '372816049',
    bank: {
      bankName: 'Lone Star Commerce Bank',
      accountMasked: '•••• 5039',
      verified: true,
    },
    ecoVadis: 59,
  },
  'sup-halbrook': {
    id: 'sup-halbrook',
    name: 'Halbrook & Partners LLP',
    shortName: 'Halbrook',
    hq: { city: 'Boston, MA', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-prof-services',
    segment: 'Preferred',
    spendTtm: 3_900_000,
    otif: null,
    ppm: null,
    riskScore: 16,
    riskLevel: 'low',
    riskNote: 'No material risk signals.',
    contractIds: ['MSA-HALB-2023-011', 'SOW-HALB-2026-03'],
    sites: ['HQ'],
    materialIds: [],
    supplierSince: 2021,
    description:
      'Boston advisory firm delivering supply chain, tariff and regulatory consulting. Currently running the tariff and network resilience study.',
    primaryContact: { name: 'Eleanor Whitcomb', title: 'Partner' },
    paymentTerms: 'Net 60',
    certifications: ['ISO 27001:2022'],
    creditScore: 87,
    duns: '651094738',
    bank: {
      bankName: 'Beacon Hill Trust',
      accountMasked: '•••• 6612',
      verified: true,
    },
    ecoVadis: 66,
  },
  'sup-brightline': {
    id: 'sup-brightline',
    name: 'Brightline Creative Group',
    shortName: 'Brightline',
    hq: { city: 'Chicago, IL', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-marketing',
    segment: 'Preferred',
    spendTtm: 6_800_000,
    otif: null,
    ppm: null,
    riskScore: 13,
    riskLevel: 'low',
    riskNote: 'No material risk signals.',
    contractIds: ['MSA-BRTL-2025-004'],
    sites: ['HQ'],
    materialIds: [],
    supplierSince: 2022,
    description:
      'Chicago creative and media agency producing campaigns and media buying for B2B electrical and industrial brands, including the Pillar Pro private label.',
    primaryContact: { name: 'Marisol Vega', title: 'Group Account Director' },
    paymentTerms: 'Net 45',
    certifications: ['ISO 27001:2022'],
    creditScore: 76,
    duns: '224760581',
    bank: {
      bankName: 'Lakefront National Trust',
      accountMasked: '•••• 1873',
      verified: true,
    },
    ecoVadis: 62,
    diverseSupplier: 'Women-owned business enterprise',
  },
  'sup-transcon': {
    id: 'sup-transcon',
    name: 'Transcontinental Freight Co.',
    shortName: 'Transcontinental',
    hq: { city: 'Memphis, TN', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-logistics',
    segment: 'Strategic',
    spendTtm: 21_400_000,
    otif: 94.5,
    otifLabel: 'On-time pickup',
    ppm: null,
    riskScore: 27,
    riskLevel: 'low',
    riskNote:
      'Service levels stable. Fuel surcharge and extra-charge billing need monitoring against the contract caps.',
    contractIds: ['MSA-TRAN-2025-006'],
    sites: ['DAL', 'PHX', 'HAY'],
    materialIds: [],
    supplierSince: 2017,
    description:
      'North American truckload and partial-load carrier moving inbound vendor freight and DC-to-branch replenishment across the Southwest.',
    primaryContact: {
      name: 'Curtis Washington',
      title: 'Director, Key Accounts',
    },
    paymentTerms: 'Net 45',
    certifications: [
      'ISO 9001:2015',
      'Customs security certified (Tier 3)',
      'SmartWay Partner',
    ],
    creditScore: 82,
    duns: '409375126',
    bank: {
      bankName: 'Delta River Bank',
      accountMasked: '•••• 4290',
      verified: true,
    },
    ecoVadis: 68,
  },
  'sup-keystone': {
    id: 'sup-keystone',
    name: 'Keystone Staffing Group',
    shortName: 'Keystone',
    hq: { city: 'Fort Worth, TX', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-contingent',
    segment: 'Preferred',
    spendTtm: 8_700_000,
    otif: null,
    ppm: null,
    riskScore: 19,
    riskLevel: 'low',
    riskNote:
      'Markup cap and overtime approval rules are reviewed on every invoice. A California wage change at Hayward DC is being assessed for pass-through cost.',
    contractIds: ['MSA-KEYS-2024-010'],
    sites: ['DAL', 'HAY', 'PNL'],
    materialIds: [],
    supplierSince: 2018,
    description:
      'Contingent labor and staffing provider for warehouse, panel shop and administrative roles.',
    primaryContact: {
      name: 'Diane Kowalski',
      title: 'Regional Vice President',
    },
    paymentTerms: 'Net 45',
    certifications: ['ISO 9001:2015', 'SOC 1 Type II'],
    creditScore: 72,
    duns: '830517264',
    bank: {
      bankName: 'Trinity River Bank',
      accountMasked: '•••• 9157',
      verified: true,
    },
    ecoVadis: 55,
    diverseSupplier: 'Women-owned business enterprise',
  },
  'sup-workform': {
    id: 'sup-workform',
    name: 'Workform Interiors',
    shortName: 'Workform',
    hq: { city: 'Dallas, TX', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-facilities',
    segment: 'Approved',
    spendTtm: 1_200_000,
    otif: null,
    ppm: null,
    riskScore: 11,
    riskLevel: 'low',
    riskNote: 'No contract in place. Spend is below the sourcing threshold.',
    contractIds: [],
    sites: ['HQ'],
    materialIds: [],
    supplierSince: 2022,
    description:
      'Office furniture and workplace interiors supplier for the Dallas headquarters and branch offices.',
    primaryContact: { name: 'Ravi Menon', title: 'Account Executive' },
    paymentTerms: 'Net 30',
    certifications: [],
    creditScore: 68,
    duns: '917263840',
    bank: {
      bankName: 'White Rock Commerce Bank',
      accountMasked: '•••• 3748',
      verified: true,
    },
    ecoVadis: null,
    diverseSupplier: 'Minority-owned business enterprise',
  },
  'sup-clearview': {
    id: 'sup-clearview',
    name: 'Clearview Facility Services',
    shortName: 'Clearview',
    hq: { city: 'Dallas, TX', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-facilities',
    segment: 'Preferred',
    spendTtm: 5_600_000,
    otif: null,
    ppm: null,
    riskScore: 17,
    riskLevel: 'low',
    riskNote: 'No material risk signals.',
    contractIds: ['MSA-CLVW-2024-003'],
    sites: ['HQ', 'DAL'],
    materialIds: [],
    supplierSince: 2015,
    description:
      'Dallas facility services firm providing janitorial, grounds and warehouse floor-care services.',
    primaryContact: {
      name: 'Patricia Lindqvist',
      title: 'Operations Director',
    },
    paymentTerms: 'Net 45',
    certifications: ['ISO 9001:2015', 'ISO 14001', 'Green Seal'],
    creditScore: 77,
    duns: '256091738',
    bank: {
      bankName: 'Turtle Creek Trust',
      accountMasked: '•••• 2086',
      verified: true,
    },
    ecoVadis: 57,
    diverseSupplier: 'Small business',
  },
  'sup-hanover': {
    id: 'sup-hanover',
    name: 'Hanover Leuchten GmbH',
    shortName: 'Hanover',
    hq: { city: 'Hanover', country: 'Germany' },
    type: 'direct',
    categoryId: 'cat-lighting',
    segment: 'Prospect',
    spendTtm: 0,
    otif: null,
    ppm: null,
    riskScore: 26,
    riskLevel: 'low',
    riskNote: 'LED high-bay bidder. Initial screening is clear.',
    contractIds: [],
    sites: [],
    materialIds: [],
    supplierSince: null,
    description:
      'German LED light fixture manufacturer and bidder in the Pillar Pro LED high-bay 2027-2029 bids.',
    primaryContact: {
      name: 'Jonas Reinhardt',
      title: 'Sales Director, Industrial Lighting',
    },
    paymentTerms: 'Net 45 (proposed)',
    certifications: [
      'UL listed (light fixtures)',
      'EU safety certified',
      'ISO 9001:2015',
      'ISO 14001',
    ],
    creditScore: 75,
    duns: '739150462',
    bank: {
      bankName: 'Leine Handelsbank',
      accountMasked: '•••• 5901',
      verified: false,
    },
    ecoVadis: 67,
  },
  'sup-mro-supply': {
    id: 'sup-mro-supply',
    name: 'MRO Supply Co.',
    shortName: 'MRO Supply',
    hq: { city: 'Houston, TX', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-mro',
    segment: 'Preferred',
    spendTtm: 9_600_000,
    otif: 95.4,
    ppm: 240,
    riskScore: 24,
    riskLevel: 'low',
    riskNote:
      'Stable Houston distributor with no open findings. Invoices arrive under its parent billing entity, Apex Industrial Group, so invoice matching relies on a saved alternate name.',
    contractIds: ['SA-MROS-2025-004'],
    sites: ['DAL', 'PHX'],
    materialIds: [],
    supplierSince: 2017,
    description:
      'Houston distributor of maintenance and industrial supplies, supplying hydraulic fittings and couplers, strut channel, brackets, valves and fasteners to the Dallas and Phoenix DCs. The Replenishment board buys from it, with prepaid freight on orders of $5,000 or more.',
    primaryContact: {
      name: 'Marie Hollis',
      title: 'Billing and Accounts Receivable',
    },
    paymentTerms: '2% off in 10 days, Net 30',
    certifications: ['ISO 9001:2015', 'Contractor safety verified'],
    creditScore: 77,
    duns: '608214937',
    bank: {
      bankName: 'Gulf Coast Commerce Bank',
      accountMasked: '•••• 5532',
      verified: true,
    },
    ecoVadis: 58,
  },
  'sup-border-states': {
    id: 'sup-border-states',
    name: 'Border States',
    shortName: 'Border States',
    hq: { city: 'San Antonio, TX', country: 'United States' },
    type: 'direct',
    categoryId: 'cat-conduit',
    segment: 'Preferred',
    spendTtm: 7_900_000,
    otif: 96.8,
    ppm: 70,
    riskScore: 16,
    riskLevel: 'low',
    riskNote:
      'No open findings. Used for fill-in and backorder supply rather than as a primary source, so Pillar has no volume dependence on it.',
    contractIds: ['SA-BRST-2024-011'],
    sites: ['DAL', 'PHX'],
    materialIds: [],
    supplierSince: 2016,
    description:
      'Electrical distributor that Pillar buys from for fill-in and backorder supply of steel conduit, boxes and flexible conduit fittings when its primary conduit sources are short.',
    primaryContact: { name: 'Tyler Brandt', title: 'Inside Sales Manager' },
    paymentTerms: 'Net 45',
    certifications: ['ISO 9001:2015'],
    creditScore: 83,
    duns: '721504839',
    bank: {
      bankName: 'Alamo Heritage Bank',
      accountMasked: '•••• 8814',
      verified: true,
    },
    ecoVadis: 64,
  },
  'sup-brazos': {
    id: 'sup-brazos',
    name: 'Brazos Valley Logistics',
    shortName: 'Brazos Valley',
    hq: { city: 'Bryan, TX', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-logistics',
    segment: 'Preferred',
    spendTtm: 2_100_000,
    otif: null,
    ppm: null,
    riskScore: 36,
    riskLevel: 'medium',
    riskNote:
      'Small regional provider that runs from a single warehouse in Bryan, so a site outage would stop pallet handling and storage for the overflow program.',
    contractIds: ['MSA-BRZV-2025-002'],
    sites: ['DAL'],
    materialIds: [],
    supplierSince: 2021,
    description:
      'Bryan, Texas third-party logistics provider handling inbound pallets, ambient storage and export crating for overflow inventory ahead of the Dallas DC.',
    primaryContact: { name: 'Wade Pruitt', title: 'Operations Manager' },
    paymentTerms: 'Net 45',
    certifications: ['ISO 9001:2015', 'Customs security certified (Tier 2)'],
    creditScore: 69,
    duns: '395827614',
    bank: {
      bankName: 'Brazos Community Bank',
      accountMasked: '•••• 2096',
      verified: true,
    },
    ecoVadis: 52,
    diverseSupplier: 'Small business',
  },
  'sup-sentinel': {
    id: 'sup-sentinel',
    name: 'Sentinel Freight',
    shortName: 'Sentinel',
    hq: { city: 'Irving, TX', country: 'United States' },
    type: 'indirect',
    categoryId: 'cat-logistics',
    segment: 'Approved',
    spendTtm: 1_400_000,
    otif: null,
    ppm: null,
    riskScore: 29,
    riskLevel: 'low',
    riskNote:
      'Reliable regional partial-load carrier. Freight is billed separately from the product invoice, so each freight invoice is matched to its shipment and proof of delivery.',
    contractIds: ['MSA-SNTL-2025-003'],
    sites: ['DAL'],
    materialIds: [],
    supplierSince: 2020,
    description:
      'Irving, Texas partial-load and truckload carrier moving inbound vendor freight from Houston to the Dallas DC, with a signed proof of delivery for every shipment.',
    primaryContact: { name: 'Rhonda Pike', title: 'Dispatch Supervisor' },
    paymentTerms: 'Net 15',
    certifications: ['SmartWay Partner', 'ISO 9001:2015'],
    creditScore: 74,
    duns: '864159270',
    bank: {
      bankName: 'Las Colinas State Bank',
      accountMasked: '•••• 6403',
      verified: true,
    },
    ecoVadis: 57,
  },
};

const SUPPLIER_IDS: string[] = Object.keys(SUPPLIERS);

/** Type guard for route params and other untyped input. */
export function isSupplierId(value: string): value is SupplierId {
  return SUPPLIER_IDS.includes(value);
}

export function getSupplier(id: SupplierId): Supplier {
  return SUPPLIERS[id];
}

/** The 26 curated suppliers, highest TTM spend first. Excludes the RFP prospect. */
export const SUPPLIER_LIST: Supplier[] = Object.values(SUPPLIERS)
  .filter((s) => s.segment !== 'Prospect')
  .sort((a, b) => b.spendTtm - a.spendTtm);

/** Bidders and candidates that are not yet suppliers. */
export const PROSPECT_SUPPLIERS: Supplier[] = Object.values(SUPPLIERS).filter(
  (s) => s.segment === 'Prospect',
);

export function suppliersByCategory(categoryId: CategoryId): Supplier[] {
  return SUPPLIER_LIST.filter((s) => s.categoryId === categoryId);
}

export function suppliersByType(type: ProcurementType): Supplier[] {
  return SUPPLIER_LIST.filter((s) => s.type === type);
}
